import { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { cva } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import AnimatedTextSwitch from '@/components/elements/AnimatedTextSwitch';
import { mergeClassName } from '@/utils/mergeClassName';

type ButtonProps = {
  variant?: 'gradient' | 'hollow' | 'disabled' | 'faded' | 'icon';
  isLoading?: boolean;
  hoverText?: string;
  className?: string;
  buttonText?: string | JSX.Element;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const buttonVariants = cva(
  'overflow-y-hidden transition-all flex items-center justify-center my-2 font-bold h-10 rounded px-4 py-2 text-center',
  {
    variants: {
      variant: {
        gradient: 'text-white w-30 bg-gradient-to-r from-[#08B3BD] via-20% to-[#7AD817] to-90% hover:opacity-85',
        hollow:
          'text-[#7AD817] border border-[#7AD817] focus:ring-4 focus:outline-none focus:ring-[#7AD817] hover:text-white hover:bg-[#7AD817]',
        disabled: 'text-slate-300 border border-slate-300 pointer-events-none',
        faded:
          'text-slate-500 border border-slate-500 focus:ring-4 focus:outline-none focus:ring-[#7AD817] hover:text-white hover:bg-slate-500',
        icon: 'w-10 !p-0 m-0 bg-gray-100 hover:bg-gray-300',
      },
    },
    defaultVariants: {
      variant: 'gradient',
    },
  }
);

const Button = ({ buttonText, hoverText, variant, isLoading = false, className, ...props }: ButtonProps) => {
  const { children, disabled } = props;
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <button
      type='button'
      className={mergeClassName([buttonVariants({ variant }), className])}
      style={{ opacity: disabled ? '0.7' : undefined, pointerEvents: disabled ? 'none' : undefined }}
      onMouseEnter={hoverText ? () => setIsHovered(true) : undefined}
      onMouseLeave={hoverText ? () => setIsHovered(false) : undefined}
      {...props}
    >
      {isLoading ? (
        <FaSpinner className='relative inline w-5 h-5 me-3 text-gray-200 animate-spin dark:text-gray-200' />
      ) : hoverText ? (
        <AnimatedTextSwitch
          secondaryText={hoverText}
          showSecondary={isHovered}
          primaryText={
            <>
              {buttonText} {children}
            </>
          }
        />
      ) : (
        <>
          {buttonText} {children}
        </>
      )}
    </button>
  );
};

export default Button;
